import { useRef, useEffect, memo } from 'react';
import { Chart, ArcElement, Tooltip, Legend, DoughnutController } from 'chart.js';
import { LOCAL_STORAGE_KEYS, THEME_TYPE } from '../../utils/global_variables';
import { getDecryptLocalStorage } from '../../utils/global_functions';

// Register required components
Chart.register(ArcElement, Tooltip, Legend, DoughnutController);

const HealthScoreDonutChart = ({ healthScore, status }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = getDecryptLocalStorage(LOCAL_STORAGE_KEYS.THEME);
    useEffect(() => {
        const ctx = chartRef.current.getContext('2d');

        // Define colors for statuses
        const statusColors = {
            "Poor": "#e95252",
            "Fair": "#febb4c",
            "Good": "#67b76a",
            "Excellent": "#4ea251"
        };

        // Data for the donut chart
        const fillValue = healthScore; // Filled percentage
        const emptyValue = 100 - fillValue; // Remaining percentage

        const chartData = {
            labels: ['Health Score', 'Empty'], // Labels for segments (tooltips will ignore this)
            datasets: [
                {
                    data: [fillValue, emptyValue], // Data for filled and empty sections
                    backgroundColor: [statusColors[status], '#ddefde'], // Colors for segments
                    borderWidth: 0, // No border
                },
            ],
        };

        // Chart options
        const options = {
            responsive: true,
            cutout: '95%', // Adjust thickness of the ring
            plugins: {
                tooltip: {
                    enabled: true, // Enable tooltips
                    backgroundColor: 'white', // Tooltip background color
                    titleColor: 'black', // Tooltip title color
                    bodyColor: 'black', // Tooltip body text color
                    borderColor: 'black', // Tooltip border color
                    borderWidth: 1, // Tooltip border width
                    displayColors: false, // Disable color boxes in the tooltip
                    callbacks: {
                        // Show only numeric values in the tooltip
                        label: (tooltipItem) => {
                            const value = tooltipItem.raw; // Get the numeric value
                            return `${value}%`; // Show only the value
                        },
                        title: () => null, // Remove the title (no "Health Score" or "Empty")
                    },
                },
                legend: {
                    display: false, // Hide legend
                },
                datalabels: {
                    display: false, // This disables datalabels plugin (if installed)
                },
            },
            elements: {
                arc: {
                    borderWidth: 0, // Ensure clean arcs without borders
                },
            },
        };

        // Plugin for center text
        const centerTextPlugin = {
            id: 'centerText',
            beforeDraw: (chart) => {
                const { width, height } = chart;
                const ctx = chart.ctx;
                ctx.restore();

                // Draw the status text in the center
                const centerX = width / 2;
                const centerY = height / 2;

                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Draw health score percentage below status
                ctx.font = '16px rubikmedium';
                ctx.fillStyle = theme === THEME_TYPE.DARK ? "white" : 'black'; // Text color for percentage
                ctx.fillText(`${fillValue}`, centerX, centerY - 10); // Percentage text above
                // Draw status
                ctx.font = '17px rubikmedium';
                ctx.fillStyle = theme === THEME_TYPE.DARK ? "white" : statusColors[status]; // Text color for status
                ctx.fillText(status, centerX, centerY + 15); // Status text below

                ctx.save();
            },
        };

        // Destroy existing chart instance if it exists
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // Create the chart instance
        chartInstance.current = new Chart(ctx, {
            type: 'doughnut',
            data: chartData,
            options: options,
            plugins: [centerTextPlugin],
        });

        // Cleanup on unmount
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [healthScore, status]);

    return <canvas ref={chartRef} width="400" height="400"></canvas>;
};

export default memo(HealthScoreDonutChart);
